import { useCallback, useEffect, useState } from 'react';
import type { ProgrammaEvento } from '@eventi/tipi.ts';
import { eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import type { BigliettoEvento, Etichetta, Evento, PartecipanteEvento, StatoLetturaOrdini } from '../../lib/eventi/tipi';

/**
 * Tutto quello che serve alla pagina di un evento: l'evento, il suo
 * programma, i partecipanti, i biglietti comprati sul sito e le etichette di
 * Mailing (per le impostazioni).
 * Ogni scheda, dopo un salvataggio, rilegge solo la parte che ha cambiato.
 */
export function useEvento(id: string | undefined) {
  const [evento, setEvento] = useState<Evento | null>(null);
  const [programma, setProgramma] = useState<ProgrammaEvento | null>(null);
  const [partecipanti, setPartecipanti] = useState<PartecipanteEvento[]>([]);
  const [etichette, setEtichette] = useState<Etichetta[]>([]);
  const [biglietti, setBiglietti] = useState<BigliettoEvento[]>([]);
  const [statoLettura, setStatoLettura] = useState<StatoLetturaOrdini | null>(null);
  const [erroreBiglietti, setErroreBiglietti] = useState<string | null>(null);
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);

  const ricaricaEvento = useCallback(async () => {
    if (!id) return;
    const letto = await eventiService.carica(id);
    setEvento(letto.evento);
    setProgramma(letto.programma);
  }, [id]);

  const ricaricaPartecipanti = useCallback(async () => {
    if (!id) return;
    setPartecipanti(await eventiService.partecipanti(id));
  }, [id]);

  // I biglietti a parte: se non si riescono a leggere la pagina dell'evento
  // si apre lo stesso, e la scheda Biglietti dice cosa non va.
  const ricaricaBiglietti = useCallback(async () => {
    if (!id) return;
    try {
      const [letti, stato] = await Promise.all([eventiService.biglietti(id), eventiService.statoLetturaOrdini()]);
      setBiglietti(letti);
      setStatoLettura(stato);
      setErroreBiglietti(null);
    } catch (e) {
      setErroreBiglietti(messaggioErrore(e));
    }
  }, [id]);

  const caricaTutto = useCallback(async () => {
    if (!id) return;
    setCaricando(true);
    setErrore(null);
    try {
      const [letto, persone, tag] = await Promise.all([
        eventiService.carica(id),
        eventiService.partecipanti(id),
        eventiService.etichette(),
        ricaricaBiglietti(),
      ]);
      setEvento(letto.evento);
      setProgramma(letto.programma);
      setPartecipanti(persone);
      setEtichette(tag);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricando(false);
    }
  }, [id, ricaricaBiglietti]);

  useEffect(() => {
    void caricaTutto();
  }, [caricaTutto]);

  return {
    evento,
    programma,
    partecipanti,
    etichette,
    biglietti,
    statoLettura,
    erroreBiglietti,
    caricando,
    errore,
    caricaTutto,
    ricaricaEvento,
    ricaricaPartecipanti,
    ricaricaBiglietti,
  };
}
